import { useContext, useEffect } from "react";
import { OrderIdContext, StageContext } from "../contexes/OrderIdContext";
import { AccountingEventsGroup } from "../types/events";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../types/documents";
import { useMarketplaceId } from "../contexes/MarketplaceIdContext";
import { getCountryCodeFromMarketplace } from "../helpers/marketplaceUtils";
import { useHealthStatus } from "./useHealthStatus";
import {
  isAccountingEventsGroup,
  isCanonicalBusinessEvent,
  isDigitalBusinessDocument,
  isDigitalCostDocument,
  isDigitalPaymentDocument,
} from "../helpers/typeChecks";
import { DlqInfo } from "./useFetchDlqInfo";
import { useGlProduct } from "../contexes/GlProductContext";
import { getPaymentMethod } from "../helpers/AccountingEventsUtils";
import { getDbdPaymentMethodName } from "../helpers/PaymentUtils";
import {
  useHealthState,
  useHealthStateUpdate,
} from "../contexes/HealthStateContext";
import { isFreeTrial } from "../helpers/freeTrialUtils";
import { shouldHideAccountingFlow } from "../helpers/flowTypeUtils";
import { CanonicalBusinessEvent } from "../types/cbes";
import { MatchSetData } from "./useFetchMatchSets";

export const GL_MAPPING: Record<string, string> = {
  "318": "TVOD",
  "613": "Channels",
  "628": "SVOD",
  "796": "SWM",
};

export const getGlMapping = (gl: number | string): string => {
  if (typeof gl === "string" && gl.includes("(")) {
    return gl;
  }

  if (gl === undefined || gl === null) {
    return "-";
  }

  const glString = gl.toString();
  if (!GL_MAPPING[glString]) {
    return glString;
  }

  const glName = GL_MAPPING[glString];
  return `${glName} (${glString})`;
};

export function useSummaryData(
  eventData: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | DigitalPaymentDocument
  )[],
  dlqInfo: DlqInfo,
  cbeData?: CanonicalBusinessEvent[],
  matchSetData?: MatchSetData
) {
  const stage = useContext(StageContext);
  const orderId = useContext(OrderIdContext);
  const { marketplaceId } = useMarketplaceId();
  const { setGlProduct } = useGlProduct();
  const countryCode = getCountryCodeFromMarketplace(marketplaceId);

  const { healthSteps, flowSteps } = useHealthStatus(dlqInfo.status);

  const healthStateContext = useHealthState();
  const { setState: setHealthState } = useHealthStateUpdate();
  const healthStatus = healthStateContext.overallHealth;

  // Show loading spinner when health status is unknown (initial state) or DLQ info is still loading
  const isHealthLoading = healthStatus === "unknown";

  const freeTrialStatus = isFreeTrial(eventData);

  const paymentFlow = cbeData?.find(
    (cbe) => cbe.internalAttributes?.paymentFlow
  )?.internalAttributes?.paymentFlow;

  const paymentMethodClass = cbeData?.find(
    (cbe) => cbe.internalAttributes?.paymentMethodClass
  )?.internalAttributes?.paymentMethodClass;

  const orderDetails = (() => {
    if (!eventData || eventData.length === 0) {
      return {
        orderDate: undefined,
        glProductGroup: undefined,
        consumptionType: undefined,
        fulfillmentType: undefined,
        lastItem: undefined,
        paymentMethod: undefined,
        isMgm: undefined,
        isBundleImmediate: undefined,
        isSeasonPassBundle: undefined,
        isPreorderBundle: undefined,
        countryCodeOverride: undefined,
      };
    }

    // Iterate in reverse to use latest versions first (as have the most data)
    for (let i = eventData.length - 1; i >= 0; i--) {
      const event = eventData[i];

      // Handle DBD and DCDs first
      if (isDigitalBusinessDocument(event)) {
        try {
          const firstProduct =
            event.body.body.products &&
            Object.values(event.body.body.products)[0];
          const paymentId = Object.keys(event.body.body.payments || {})[0];
          const payment = event.body.body.payments?.[paymentId];
          const method = payment?.paymentMethods?.[paymentId];
          const isBundleImmediate =
            event.body.metadata.internalAttributes?.IS_BUNDLE_IMMEDIATE ===
            "true";
          const isSeasonPassBundle =
            event.body.metadata.internalAttributes?.IS_SEASON_PASS_BUNDLE ===
            "true";
          const isPreorderBundle =
            event.body.metadata.internalAttributes?.IS_PREORDER_BUNDLE ===
            "true";

          return {
            orderDate: event.body.header.transactionDate,
            glProductGroup: firstProduct?.details.glProductLineId,
            consumptionType: firstProduct?.details.activity,
            fulfillmentType:
              event.body.metadata.internalAttributes?.FULFILLMENT_TYPE,
            lastItem: { digitalBusinessDocument: event },
            paymentMethod: method ? getDbdPaymentMethodName(method) : undefined,
            isBundleImmediate,
            isSeasonPassBundle,
            isPreorderBundle,
          };
        } catch (error) {
          console.error("Failed to process DBD data:", error);
          return {
            orderDate: undefined,
            glProductGroup: undefined,
            consumptionType: undefined,
            fulfillmentType: undefined,
            lastItem: { digitalBusinessDocument: event },
            paymentMethod: undefined,
            isBundleImmediate: undefined,
            isSeasonPassBundle: undefined,
            isPreorderBundle: undefined,
          };
        }
      }

      if (isDigitalCostDocument(event)) {
        return {
          orderDate: event.body.header.transactionDate,
          glProductGroup: event.body.body.glProductLineId,
          consumptionType: event.body.body.cost.transactionType,
          lastItem: { digitalCostDocument: event },
          isBundleImmediate: undefined,
          isSeasonPassBundle: undefined,
          isPreorderBundle: undefined,
        };
      }

      if (isDigitalPaymentDocument(event)) {
        return {
          orderDate: event.body.body.glDate,
          glProductGroup: event.body.body.glProductLineId,
          consumptionType: undefined,
          fulfillmentType: undefined,
          lastItem: { digitalPaymentDocument: event },
          paymentMethod: undefined,
          isBundleImmediate: undefined,
          isSeasonPassBundle: undefined,
          isPreorderBundle: undefined,
          countryCodeOverride: event.body.body.offerTerritoryCountryCode,
        };
      }

      if (isAccountingEventsGroup(event)) {
        const revenueEvent = Object.values(event.accountingEvents).find(
          (acctEvent) => acctEvent.type === "REVENUE"
        );
        const fallbackEvent = Object.values(event.accountingEvents).find(
          (acctEvent) => acctEvent.type === "ROYALTY"
        );
        const selectedEvent = revenueEvent || fallbackEvent;

        // Only return accounting events if they shouldn't be hidden
        if (!shouldHideAccountingFlow(eventData, cbeData ?? [])) {
          return {
            orderDate:
              selectedEvent?.orderDate || selectedEvent?.contractStartDate,
            glProductGroup: selectedEvent?.glProductGroup,
            consumptionType: revenueEvent?.consumptionType,
            lastItem: { accountingEventsGroup: event },
            paymentMethod: revenueEvent
              ? getPaymentMethod(revenueEvent)
              : undefined,
            isBundleImmediate: undefined,
            isSeasonPassBundle: undefined,
            isPreorderBundle: undefined,
            declaredCountryCode: selectedEvent?.declaredCountryCode,
          };
        }
      }

      if (isCanonicalBusinessEvent(event)) {
        return {
          isMgm: event.vendorName === "MGM",
          orderDate: undefined,
          glProductGroup: undefined,
          consumptionType: undefined,
          fulfillmentType: undefined,
          lastItem: undefined,
          paymentMethod: undefined,
          isBundleImmediate: undefined,
          isSeasonPassBundle: undefined,
          isPreorderBundle: undefined,
        };
      }
    }

    return {
      orderDate: undefined,
      glProductGroup: undefined,
      consumptionType: undefined,
      paymentMethod: undefined,
      lastItem: undefined,
      isBundleImmediate: false,
      isSeasonPassBundle: false,
      isPreorderBundle: false,
      isMgm: false,
      countryCodeOverride: undefined,
    };
  })();

  setGlProduct(
    orderDetails.glProductGroup
      ? getGlMapping(orderDetails.glProductGroup.toString())
      : "unknown"
  );

  useEffect(() => {
    setHealthState((prevState) => ({
      ...prevState,
      isFreeTrial: freeTrialStatus,
    }));
  }, [freeTrialStatus, setHealthState]);

  // Match sets have their own dedicated status column and do not affect overall health.
  const matchSetTotal = matchSetData?.total ?? 0;
  const matchSetLoading = matchSetData?.isLoading ?? true;
  const matchSetAllClosed = matchSetData?.allClosed ?? false;
  const matchSetOpenCount = matchSetData?.openCount ?? 0;

  useEffect(() => {
    if (!matchSetLoading && matchSetTotal > 0) {
      setHealthState((prevState) => ({
        ...prevState,
        matchSetStatus: {
          allClosed: matchSetAllClosed,
          openCount: matchSetOpenCount,
          total: matchSetTotal,
        },
      }));
    }
  }, [
    matchSetTotal,
    matchSetLoading,
    matchSetAllClosed,
    matchSetOpenCount,
    setHealthState,
  ]);

  return {
    stage,
    orderId,
    countryCode: orderDetails.countryCodeOverride || countryCode,
    healthStatus,
    healthSteps,
    orderDate: orderDetails.orderDate,
    glProductGroup: orderDetails.glProductGroup,
    consumptionType: orderDetails.consumptionType,
    fulfillmentType: orderDetails.fulfillmentType,
    paymentFlow,
    paymentMethodClass,
    paymentMethod: orderDetails.paymentMethod,
    isFreeTrial: freeTrialStatus,
    isBundleImmediate: orderDetails.isBundleImmediate,
    isSeasonPassBundle: orderDetails.isSeasonPassBundle,
    isPreorderBundle: orderDetails.isPreorderBundle,
    getGlMapping,
    lastItem: orderDetails.lastItem,
    isHealthLoading,
    flowSteps,
    isMgm: orderDetails.isMgm,
    declaredCountryCode: orderDetails.declaredCountryCode,
  };
}
